import React, {useEffect, useState} from "react"
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {pair} from "@/library/types/pair"
import useAuth from "@/library/auth/_hook.auth"
import {Product, ProductVersion, ProductVersionUpdate} from "@/library/api/models/changelog"
import {pckChangelog, pckProductVersionDelete} from "@/library/api/changelog-requests"
import {classNames} from "@/library/utilities/html"
import Button from "@/components/ui-kit/button"
import TimeStamp from "@/components/timestamp"
import useChangelog from "@/components/changelog/context/_hook.changelog"
import ChangelogItemChange from "@/components/changelog/changelog-item-change"
import Form from "@/components/forms/form"
import ChangelogForm from "@/components/changelog/changelog-form"
import parse from "html-react-parser"

export default function ChangelogItem(props: {
    index?: number,
    version: ProductVersion,
    skeleton?: boolean,
    onRemove?: (id: number) => void
}) {
    const {currentProduct, withAuth} = useChangelog()
    const {jwtData, roles} = useAuth()
    const {version, skeleton} = props
    const [editMode, setEditMode] = useState(false)
    const [permissions, setPermissions] = useState<{
        canUpdate: boolean
        canDelete: boolean
        canCreate: boolean
    }>({
        canUpdate: false,
        canCreate: false,
        canDelete: false
    })

    useEffect(() => {
        if (withAuth == false)
            return

        setPermissions(() => {
            return {
                canUpdate: roles.includes("UpdateChangelogEntry"),
                canDelete: roles.includes("DeleteChangelogEntry"),
                canCreate: roles.includes("CreateChangelogEntry")
            }
        })
    }, [jwtData])

    const queryClient = useQueryClient()
    const mutation = useMutation({
        mutationFn: async () => {
            return await pckProductVersionDelete(version.id)
        },
        onSuccess: () => {
            queryClient.setQueryData(['changes', version.product.refKey], (currentData: ProductVersion[]) => {
                return currentData.filter(change => change.id != version.id)
            })
        }
    })

    const parseDescription = (description: string) => {
        const pattern = /<[\w]+>(.*)<\/[\w]+>/

        if (pattern.test(description)) {
            return parse(description)
        }

        return parse(`<p>${description.replaceAll("\n", "<br />")}</p>`)
    }

    return <div
        id={version.number}
        className={classNames(pair("product-version", !editMode), pair("skeleton", skeleton))}
    >
        {editMode && <ChangelogForm<ProductVersionUpdate>
            initialValues={{
                id: version.id,
                number: version.number,
                changes: version.changes,
                released: version.released,
                productId: version.product.id,
                branchId: version.branch.id,
                description: version.description
            }}
            closeCallback={() => setEditMode(() => false)}
        />}
        {!editMode && <>
            <div className="version-details">
                <div className="product">{version.product?.name}</div>
                <div className="number">{version.number}</div>
                <div className="date">
                    {version.released && <TimeStamp dateOnly={true} timestamp={version.released}/>}
                    {(!version.released && !skeleton) && <span className="upcoming">Kommer</span>}
                </div>
                <div className="branch">{version.branch?.name}</div>
            </div>
            {version.description && <div className="version-description">{parseDescription(version.description)}</div>}
            <div className="change-list">
                {version.description && <h3>Endringer</h3>}
                {(!skeleton && (version.changes?.length ?? 0) <= 0) &&
                    <div className="alert alert--info">Ingen endringer funnet for denne versjonen...</div>
                }
                {version.changes?.map(change => <ChangelogItemChange key={change.id} change={change}/>)}
            </div>
            {(permissions.canDelete || permissions.canUpdate || permissions.canDelete) && <div className="version-toolbar">
                {permissions.canDelete && <Button color="danger" onClick={() => {
                    mutation.mutate()
                }}><i className="far fa-trash"/> Slett</Button>}
                {permissions.canUpdate && <Button color="primary" onClick={() => {
                    setEditMode(() => true)
                }}><i className="far fa-pencil"/> Rediger</Button>}
            </div>}
        </>}
    </div>
}
